import { useEffect, useState, useMemo } from 'react'
import { useSearchParams, useLocation } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useTodayEvents, useUpcomingEvents } from '../hooks/useEvents'
import ProtectedRoute from '../components/auth/ProtectedRoute'
import { CurrentAgenda, EventList, AppHeader } from '../components'
import { User, Calendar, Clock } from 'lucide-react'
import { Helmet } from 'react-helmet-async'

export default function EventsPage() {
  const { user } = useAuth()
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const [isClient, setIsClient] = useState(false)

  // Fetch event data for Quick Stats
  const { events: todayEvents } = useTodayEvents()
  const { events: upcomingEvents } = useUpcomingEvents()

  // Compute quick stats from event data
  const quickStats = useMemo(() => {
    const eventsToday = todayEvents.length
    const upcoming = upcomingEvents.length
    const signedUp = user
      ? todayEvents.filter(e => e.participants.includes(user.userId)).length +
        upcomingEvents.filter(e => e.participants.includes(user.userId)).length
      : 0

    return { eventsToday, upcoming, signedUp }
  }, [todayEvents, upcomingEvents, user])

  useEffect(() => {
    setIsClient(true)
  }, [])

  // Handle navigation from push notifications
  useEffect(() => {
    if (!isClient) return

    const handleNavigation = (event: MessageEvent) => {
      const { type, url } = event.data || {}
      
      if (type === 'navigate' && url) {
        // Use React Router navigation instead of Next.js router
        window.location.href = url
      }
    }

    // Listen for navigation messages from service worker (only in browser)
    if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
      navigator.serviceWorker?.addEventListener('message', handleNavigation)
    }

    return () => {
      if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
        navigator.serviceWorker?.removeEventListener('message', handleNavigation)
      }
    }
  }, [location, isClient])

  // Handle event ID from query parameters (from notifications)
  useEffect(() => {
    if (!isClient) return

    const eventId = searchParams.get('eventId')
    
    if (eventId) {
      // Scroll to specific event or highlight it
      const eventElement = document.getElementById(`event-${eventId}`)
      if (eventElement) {
        eventElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        eventElement.classList.add('ring-2', 'ring-fwo-blue', 'ring-opacity-50')
        
        // Remove highlight after 3 seconds
        setTimeout(() => {
          eventElement.classList.remove('ring-2', 'ring-fwo-blue', 'ring-opacity-50')
        }, 3000)
      }
    }
  }, [searchParams, isClient])

  if (!user) {
    return null
  }

  return (
    <ProtectedRoute>
      <Helmet>
        <title>Events - FWO Pilot Portal</title>
        <meta name="description" content="View and manage competition events and schedule" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        {/* Header */}
        <AppHeader activePage="events" />

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Current Agenda Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-8">
                <CurrentAgenda />
                
                {/* Quick Stats */}
                <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Stats</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">Events today</span>
                      </div>
                      <span className="text-sm font-medium text-gray-900">{quickStats.eventsToday}</span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Clock className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">Upcoming</span>
                      </div>
                      <span className="text-sm font-medium text-gray-900">{quickStats.upcoming}</span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <User className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">Signed up</span>
                      </div>
                      <span className="text-sm font-medium text-gray-900">{quickStats.signedUp}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Events List */}
            <div className="lg:col-span-2">
              <EventList />
            </div>
          </div>
        </main>
      </div>
    </ProtectedRoute>
  )
}